/**
 * Join school display names with a smart common-word-suffix merge.
 * Example: "Harrison Middle School" + "Adams Middle School" → "Harrison/Adams Middle School".
 */
export function formatMergedSchoolNames(names: readonly string[]): string {
  const trimmed = names.map((name) => name.trim()).filter((name) => name.length > 0);
  if (trimmed.length === 0) {
    return "";
  }
  if (trimmed.length === 1) {
    return trimmed[0];
  }

  const tokenLists = trimmed.map((name) => name.split(/\s+/).filter(Boolean));
  const primaryTokens = tokenLists[0];

  const allTokenSequencesEqual = tokenLists.every((tokens) => {
    if (tokens.length !== primaryTokens.length) {
      return false;
    }
    return tokens.every(
      (token, index) => token.toLowerCase() === primaryTokens[index].toLowerCase(),
    );
  });
  if (allTokenSequencesEqual) {
    return trimmed[0];
  }

  const maxSuffixLength = Math.min(...tokenLists.map((tokens) => Math.max(0, tokens.length - 1)));

  let suffixLength = 0;
  for (let offset = 1; offset <= maxSuffixLength; offset += 1) {
    const primaryToken = primaryTokens[primaryTokens.length - offset];
    const matches = tokenLists.every((tokens) => {
      const token = tokens[tokens.length - offset];
      return token != null && token.toLowerCase() === primaryToken.toLowerCase();
    });
    if (!matches) {
      break;
    }
    suffixLength = offset;
  }

  if (suffixLength === 0) {
    return trimmed.join("/");
  }

  const prefixes = tokenLists.map((tokens) => tokens.slice(0, tokens.length - suffixLength).join(" "));
  const suffix = primaryTokens.slice(primaryTokens.length - suffixLength).join(" ");
  return `${prefixes.join("/")} ${suffix}`;
}
